import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { FormGroup } from '@angular/forms';
import { Options } from '@angular-slider/ngx-slider';

import { BaseComponent } from '@components/base-component';
import { StoreDataService } from '@common/services/store-data.service';
import { FormUtilsService } from '@common/services/form-utils.service';
import { Profile } from '@common/models/profile.model';
import { URL_CONFIG } from '@constants/locations.constants';
import { PickerData } from '@common/models/picker-option.model';
import { BUTTON_NEXT } from '@constants/pages-config.constants';
import { CHECK_PROFILE_STATE_INT_VALUE } from '@constants/common.constants';

@Component({
    selector: 'app-your-clean',
    templateUrl: './your-clean.component.html',
    styleUrls: ['./your-clean.component.scss'],
    standalone: false,
})
export class YourCleanComponent extends BaseComponent implements OnInit, OnDestroy {
    override progressBarStatus = {
        progress: 1,
        completedStep: 0,
    };
    override navigationSubscription!: Subscription;
    override navigationRoutes = {
        nextRoute: this.pagesUrls[this.pages.QUOTE],
        backRoute: '',
    };
    override isNextButtonDisabled = true;
    override isNextButtonActive = false;
    contactForm: FormGroup = new FormGroup({});
    address!: string;
    city!: string;
    country!: string;
    localSiteUrl!: string;
    termsOfUseUrl!: string;
    privacyPolicyUrl!: string;

    addressLoaded = false;
    nextButtonText = 'Continue';

    squareFootage!: number;
    footageDefaultValue = 1000;
    footageStringValue = '1000';
    footageOptions: Options = {
        floor: 500,
        ceil: 4000,
        step: 500,
        translate: (value: number): string => {
            return this.formatValue(value);
        },
        showTicks: true,
    };
    formatValue(value: number): string {
        return value.toLocaleString('en-US') + ` ft²`;
    }
    levelsData: Array<PickerData> = [
        { label: 'One level', value: 1 },
        { label: 'Two levels', value: 2 },
        { label: 'Three levels', value: 3 },
        { label: 'Four levels', value: 4 },
    ];
    levelsNumber!: number;

    petsNumber!: number;
    petsInitialValue = 0;
    petsMinValue = 0;
    petsMaxValue = 10;
    petsStep = 1;

    locationName!: string;

    profileState!: Profile;
    checkProfileStateInterval!: ReturnType<typeof setInterval>;
    private focusedField: string | null = null;
    
    public onFieldFocus(fieldName: string): void {
        this.focusedField = fieldName;
    }
    
    public onFieldBlur(): void {
        this.focusedField = null;
    }
    
    get isFirstNameInvalid(): boolean {
        const control = this.contactForm.controls['firstName'];
        return control.touched && control.invalid && this.focusedField !== 'firstName';
    }
    get isLastNameInvalid(): boolean {
        const control = this.contactForm.controls['lastName'];
        return control.touched && control.invalid && this.focusedField !== 'lastName';
    }
    get isEmailInvalid(): boolean {
        const control = this.contactForm.controls['email'];
        // Don't show error if field is currently focused
        return control.touched && control.invalid && this.focusedField !== 'email';
    }
    get isPhoneInvalid(): boolean {
        const control = this.contactForm.controls['phone'];
        // Don't show error if field is currently focused
        return control.touched && control.invalid && this.focusedField !== 'phone';
    }

    constructor(
        private storeDataService: StoreDataService,
        private formUtilsService: FormUtilsService
    ) {
        super();
        this.checkProfileStateInterval = setInterval(() => {
            if (this.profileState?.locationCode) {
                const sanitizedProfile = this.formUtilsService.sanitizeProfileData(
                    this.profileState
                );
                this.storeDataService.updateProfileData(sanitizedProfile);
            }
        }, CHECK_PROFILE_STATE_INT_VALUE);
    }

    override ngOnInit(): void {
        super.ngOnInit();
        super.setBrowserBackRoute(this.navigationRoutes.backRoute);
        this.navigationService.setNextButtonLabel(this.nextButtonText);
        this.buildContactForm();
        this.handleContactForm();
        const nextButtonSubscription = this.navigationService
            .getNavigationButtonClicked()
            .subscribe((res) => {
                if (this.isNavigationButtonClicked(res.button) && !res.allowNavigation) {
                    this.storeCurrentData(res.button);
                }
            });
        const profileSubscription = this.storeDataService
            .profileObserver()
            .subscribe((profile: Profile) => {
                if (profile.profileId && !profile.skipUpdate) this.setupDefaultOptions(profile);
                delete profile.skipUpdate;
            });
        this.addSubscription(nextButtonSubscription);
        this.addSubscription(profileSubscription);
        this.navigationService.setPrevNavigationShown(false);
    }

    setupDefaultOptions(profile: Profile) {
        this.profileState = JSON.parse(JSON.stringify(profile));
        const { address, postalCode, city, state, country } = this.storeDataService.profileData;
        const suite =
            this.storeDataService.profileData.suite &&
            this.storeDataService.profileData.suite !== 'null'
                ? ', ' + this.storeDataService.profileData.suite
                : '';
        this.addressLoaded = !!address;
        this.address = `${address}${suite}, ${city}, ${state} ${postalCode}`;
        this.city = city ? city : '';
        this.country = country ? country : 'US';
        if (!profile.localSiteUrl) {
            this.localSiteUrl = URL_CONFIG[this.country].LOCAL_SITE;
            this.profileState.localSiteUrl = URL_CONFIG[this.country].LOCAL_SITE;
        } else {
            this.localSiteUrl = profile.localSiteUrl;
        }
        if (!profile.privacyPolicyUrl) {
            this.profileState.privacyPolicyUrl = URL_CONFIG[this.country].PRIVACY_POLICY;
        }
        this.privacyPolicyUrl = profile.privacyPolicyUrl || URL_CONFIG[this.country].PRIVACY_POLICY;
        if (!profile.termsOfUseUrl) {
            this.profileState.termsOfUseUrl = URL_CONFIG[this.country].TERMS_OF_USE;
        }
        this.termsOfUseUrl = profile.termsOfUseUrl || URL_CONFIG[this.country].TERMS_OF_USE;
        if (profile.firstName) {
            this.contactForm.get('firstName')?.setValue(profile.firstName);
            this.contactForm.controls['firstName'].markAsTouched();
        }
        if (profile.lastName) {
            this.contactForm.get('lastName')?.setValue(profile.lastName);
            this.contactForm.controls['lastName'].markAsTouched();
        }
        if (profile.email) {
            this.contactForm.get('email')?.setValue(profile.email);
            this.contactForm.controls['email'].markAsTouched();
        }
        if (profile.phone) {
            this.contactForm.get('phone')?.setValue(profile.phone);
            this.contactForm.controls['phone'].markAsTouched();
        }
        if (profile.petsNumber) {
            this.petsInitialValue = profile.petsNumber;
            this.petsNumber = profile.petsNumber;
        } else {
            this.profileState.petsNumber = 0;
        }
        if (profile.levels) {
            this.levelsNumber = profile.levels;
            this.handleNavigation();
        }
        if (profile.square) {
            this.squareFootage = profile.square;
            this.footageDefaultValue = profile.square;
            this.footageStringValue = profile.square.toString();
        } else {
            this.profileState.square = this.footageDefaultValue;
        }
        this.locationName = profile.locationName ? profile.locationName : '';
    }

    storeCurrentData(button: string): void {
        if (this.checkProfileStateInterval) {
            clearInterval(this.checkProfileStateInterval);
        }

        this.profileState = this.formUtilsService.sanitizeProfileData(this.profileState);

        if (this.contactForm.valid && this.levelsNumber) {
            this.googleAnalyticsService.event(
                'footage_checked',
                'footage',
                this.profileState.square + ''
            );

            this.storeDataService
                .storeCurrentData({
                    ...this.profileState,
                    url: this.navigationRoutes.nextRoute,
                })
                .subscribe(() => {
                    this.navigationService.setNavigationButtonClicked({
                        button: button,
                        allowNavigation: true,
                    });
                });
        } else {
            this.navigationService.setNavigationButtonClicked({
                button: button,
                allowNavigation: true,
            });
        }
    }

    buildContactForm(): void {
        this.contactForm = this.formUtilsService.buildContactForm();
    }

    onNameInput(event: Event): void {
        this.formUtilsService.handleNameInput(event, this.contactForm);
    }

    onEmailInput(event: Event): void {
        this.formUtilsService.handleEmailInput(event, this.contactForm);
    }

    firstNameOnBlur() {
        if (this.isFirstNameInvalid) {
            this.googleAnalyticsService.event(
                'first_name_error',
                'error_shown',
                'First Name Error'
            );
        }
    }

    lastNameOnBlur() {
        if (this.isLastNameInvalid) {
            this.googleAnalyticsService.event('last_name_error', 'error_shown', 'Last Name Error');
        }
    }

    emailOnBlur() {
        if (this.isEmailInvalid) {
            this.googleAnalyticsService.event('email_error', 'error_shown', 'Email Error');
        }
    }
    phoneOnBlur() {
        if (
            this.contactForm.controls['phone'].touched &&
            this.contactForm.controls['phone'].invalid
        ) {
            this.googleAnalyticsService.event('phone_error', 'error_shown', 'Phone Error');
        }
    }

    handleContactForm(): void {
        const contactFormSubscription = this.contactForm.valueChanges.subscribe(() => {
            this.handleNavigation();
        });
        this.addSubscription(contactFormSubscription);
    }

    onContactFormFocusOut() {
        this.formUtilsService.trimFormValues(this.contactForm);
        if (this.contactForm.valid) {
            const contactFormValues = this.formUtilsService.sanitizeContactFormValues(
                this.contactForm.value
            );

            this.profileState.firstName = contactFormValues.firstName;
            this.profileState.lastName = contactFormValues.lastName;
            this.profileState.email = contactFormValues.email;
            this.profileState.phone = contactFormValues.phone;
        }
    }

    onLevelsSelected(selectedTab: number | string): void {
        if (selectedTab) {
            this.levelsNumber = selectedTab as number;
            this.handleNavigation();
            this.profileState.levels = this.levelsNumber;
            this.googleAnalyticsService.event('levels_checked', 'levels', this.levelsNumber + '');
        }
    }

    onFootageValueChanged(value: number): void {
        this.squareFootage = value;
        this.profileState.square = this.squareFootage;
    }

    onPetsValueChange(value: number): void {
        this.petsNumber = value;
        this.profileState.petsNumber = this.petsNumber;
    }

    handleNavigation() {
        if (this.contactForm.valid && this.levelsNumber) {
            this.isNextButtonDisabled = false;
            this.isNextButtonActive = true;
            this.navigationService.setNextButtonDisabled(this.isNextButtonDisabled);
            this.navigationService.setNextButtonActive(this.isNextButtonActive);
        } else {
            this.isNextButtonDisabled = true;
            this.navigationService.setNextButtonDisabled(this.isNextButtonDisabled);
        }
    }

    sendManagerRequest() {
        if (this.checkProfileStateInterval) {
            clearInterval(this.checkProfileStateInterval);
        }
        this.googleAnalyticsService.event(
            'footage_over_4000_checked',
            'footage',
            'over 4000 sq ft'
        );
        this.profileState = this.formUtilsService.sanitizeProfileData(this.profileState);
        this.profileState.square = 4000;
        this.storeDataService
            .storeCurrentData({
                ...this.profileState,
                url: this.pagesUrls[this.pages.SPECIAL_ATTENTION],
            })
            .subscribe(() => {
                this.navigationRoutes.nextRoute = this.pagesUrls[this.pages.SPECIAL_ATTENTION];
                this.storeDataService.sendOrder('4000').subscribe(() => {
                    this.navigationService.setNavigationButtonClicked({
                        button: BUTTON_NEXT,
                        allowNavigation: true,
                    });
                });
            });
    }

    override ngOnDestroy(): void {
        if (this.checkProfileStateInterval) {
            clearInterval(this.checkProfileStateInterval);
        }
        super.ngOnDestroy();
    }
}
